import { Injectable } from '@angular/core';
import { Map } from 'immutable';
import { BehaviorSubject } from 'rxjs';

import { ErrorTypeOrder } from '../../core/domain/abs-diagnostic-error.entity';
import {
	SubscriptionDiagnosticError,
	SubscriptionErrorKey,
} from '../domain/diagnostic-errors.entity';

@Injectable()
export class SubscriptionProvider {
	private readonly errorsSubject = new BehaviorSubject<
		Map<SubscriptionErrorKey, SubscriptionDiagnosticError>
	>(Map());
	public readonly errors$ = this.errorsSubject.asObservable();
	private errorsMap: Record<number, Array<SubscriptionErrorKey>> = {};

	public configure(
		errorsMap: Record<string, Array<SubscriptionErrorKey>>
	): void {
		this.errorsMap = errorsMap;
	}

	public getErrorsForStep(
		step: number
	): Map<SubscriptionErrorKey, SubscriptionDiagnosticError> {
		const maybeErrorsPerStep = this.errorsMap[step];
		return this.filterErrors(maybeErrorsPerStep);
	}

	public getErrors(): Map<SubscriptionErrorKey, SubscriptionDiagnosticError> {
		return this.errorsSubject.value;
	}

	public addError(error: SubscriptionDiagnosticError): void {
		const currentErrors = this.getErrors();

		const errorKey = error.key as SubscriptionErrorKey;

		if (currentErrors.has(errorKey)) {
			return;
		}

		const updatedErrors = currentErrors.set(errorKey, error);

		this.errorsSubject.next(updatedErrors);
	}

	public addCriticalError(msg: string, errorKey: SubscriptionErrorKey): void {
		this.addError(
			SubscriptionDiagnosticError.build({
				key: errorKey,
				type: 'critic',
				message: msg,
			})
		);
	}

	public addWarningError(msg: string, errorKey: SubscriptionErrorKey): void {
		this.addError(
			SubscriptionDiagnosticError.build({
				key: errorKey,
				type: 'warning',
				message: msg,
			})
		);
	}

	public removeError(key: SubscriptionErrorKey): void {
		const currentErrors = this.getErrors();

		if (!currentErrors.has(key)) {
			return;
		}

		const updatedErrors = currentErrors.delete(key);

		this.errorsSubject.next(updatedErrors);
	}

	public getOrderedErrors(): Map<
		SubscriptionErrorKey,
		SubscriptionDiagnosticError
	> {
		const currentErrors = this.getErrors();
		return currentErrors.sort(
			(a, b) => ErrorTypeOrder[a.type] - ErrorTypeOrder[b.type]
		);
	}

	public getCriticalErrors(): Map<
		SubscriptionErrorKey,
		SubscriptionDiagnosticError
	> {
		const currentErrors = this.getErrors();
		return currentErrors.filter((error) => error.type === 'critic');
	}

	public filterErrors(
		errorKeys: Array<string>
	): Map<SubscriptionErrorKey, SubscriptionDiagnosticError> {
		const currentErrors = this.getErrors();
		let errors = Map<SubscriptionErrorKey, SubscriptionDiagnosticError>();
		errorKeys.forEach((key) => {
			const itemKey = key as SubscriptionErrorKey;
			const item = currentErrors.get(itemKey);

			if (item) {
				errors = errors.set(itemKey, item);
			}
		});
		return errors;
	}
}
